import { api } from './axios';

export type LeaveStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
export type ComponentKind = 'EARNING' | 'DEDUCTION';
export type PayrollStatus = 'DRAFT' | 'FINALIZED';

/* ---- leave ---- */
export interface LeaveType {
  id: number;
  name: string;
  paid: boolean;
  allowCarryForward: boolean;
  maxCarryForward: number | null;
  active: boolean;
}
export interface LeaveTypeInput {
  name: string;
  paid: boolean;
  allowCarryForward: boolean;
  maxCarryForward?: number | null;
}

export interface LeaveQuota {
  id: number;
  leaveTypeId: number;
  leaveTypeName: string | null;
  employmentTypeId: number;
  employmentTypeName: string | null;
  daysPerYear: number;
  active: boolean;
}
export interface LeaveQuotaInput {
  leaveTypeId: number;
  employmentTypeId: number;
  daysPerYear: number;
}

export interface LeaveRequest {
  id: number;
  staffId: number;
  staffName: string | null;
  leaveTypeId: number;
  leaveTypeName: string | null;
  paid: boolean;
  fromDate: string;
  toDate: string;
  totalDays: string;
  reason: string | null;
  status: LeaveStatus;
  approvedBy: number | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
}
export interface CreateLeaveRequestInput {
  staffId: number;
  leaveTypeId: number;
  fromDate: string;
  toDate: string;
  totalDays: string;
  reason?: string;
}

export interface LeaveBalance {
  id: number;
  staffId: number;
  staffName: string | null;
  leaveTypeId: number;
  leaveTypeName: string | null;
  academicYearId: number;
  academicYearName: string | null;
  allocatedDays: string;
  carriedForward: string;
  usedDays: string;
  remainingDays: string;
}

/* ---- salary + payroll ---- */
export interface SalaryComponent {
  id: number;
  name: string;
  kind: ComponentKind;
  active: boolean;
}
export interface SalaryComponentInput {
  name: string;
  kind: ComponentKind;
}

export interface StructureComponent {
  componentId: number;
  componentName: string;
  kind: ComponentKind;
  amount: string;
  isPercentage: boolean;
}
export interface SalaryStructure {
  id: number;
  staffId: number;
  staffName: string | null;
  components: StructureComponent[];
  grossSalary: string;
  totalEarnings: string;
  totalDeductions: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  revisionReason: string | null;
  createdAt: string;
}
export interface SalaryStructureInput {
  effectiveFrom: string;
  components: { componentId: number; amount: string; isPercentage: boolean }[];
  revisionReason?: string;
}

export interface SalaryAdvance {
  id: number;
  staffId: number;
  staffName: string | null;
  advanceDate: string;
  amount: string;
  repaymentMonths: number;
  monthlyEmi: string;
  recoveredAmount: string;
  outstanding: string;
  fullyRecovered: boolean;
  bankAccountId: number | null;
  bankAccountName: string | null;
  remarks: string | null;
}
export interface SalaryAdvanceInput {
  staffId: number;
  advanceDate: string;
  amount: string;
  repaymentMonths: number;
  bankAccountId: number;
  remarks?: string;
}

export interface PayrollRun {
  id: number;
  academicYearId: number;
  academicYearName: string | null;
  payrollMonth: string;
  status: PayrollStatus;
  finalizedAt: string | null;
  reversed: boolean;
  reversalReason: string | null;
  itemCount: number;
  totalGross: string;
  totalNet: string;
}

export interface PayrollItem {
  id: number;
  payrollRunId: number;
  staffId: number;
  staffName: string | null;
  salaryStructureId: number;
  salarySlipNumber: number | null;
  grossSalary: string;
  absentDeduction: string;
  unpaidLeaveDeduction: string;
  manualAdditions: string;
  manualDeductions: string;
  advanceDeduction: string;
  netSalary: string;
  remarks: string | null;
}
export interface PayrollItemInput {
  absentDeduction: string;
  unpaidLeaveDeduction: string;
  manualAdditions: string;
  manualDeductions: string;
  advanceDeduction: string;
  remarks?: string;
}

export type RatingType = 'NUMERIC' | 'LABEL';

export interface EvaluationCriteria {
  id: number;
  name: string;
  ratingType: RatingType;
  maxScore: number | null;
  sortOrder: number;
  active: boolean;
}

export interface EvaluationCriteriaInput {
  name: string;
  ratingType: RatingType;
  maxScore?: number | null;
  sortOrder: number;
}

export interface CriteriaScoreInput {
  criteriaId: number;
  criteriaName?: string | null;
  score?: string | number | null;
  label?: string | null;
}

export interface PerformanceEvaluation {
  id: number;
  staffId: number;
  staffName: string | null;
  academicYearId: number;
  academicYearName: string | null;
  evaluationPeriod: string | null;
  criteriaScores: Array<{
    criteriaId: number;
    criteriaName: string | null;
    score: string | null;
    label: string | null;
  }>;
  overallScore: string | null;
  overallLabel: string | null;
  remarks: string | null;
  evaluatedBy: number;
  evaluatedByUsername: string | null;
  sharedWithStaff: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PerformanceEvaluationInput {
  staffId: number;
  academicYearId: number;
  evaluationPeriod?: string | null;
  criteriaScores: CriteriaScoreInput[];
  remarks?: string | null;
  sharedWithStaff: boolean;
}

export interface StaffDocument {
  id: number;
  staffId: number;
  documentName: string;
  filePath: string;
  uploadedAt: string;
}

export interface StaffDocumentInput {
  documentName: string;
  filePath: string;
}

export const hrDepthApi = {
  // leave types
  listLeaveTypes: (activeOnly = false) =>
    api.get<LeaveType[]>('/hr/leave/types', { params: { activeOnly } }).then((r) => r.data),
  createLeaveType: (body: LeaveTypeInput) =>
    api.post<LeaveType>('/hr/leave/types', body).then((r) => r.data),
  updateLeaveType: (id: number, body: LeaveTypeInput) =>
    api.put<LeaveType>(`/hr/leave/types/${id}`, body).then((r) => r.data),
  toggleLeaveType: (id: number, value: boolean) =>
    api.patch<LeaveType>(`/hr/leave/types/${id}/active`, null, { params: { value } }).then((r) => r.data),

  // leave quotas
  listLeaveQuotas: () => api.get<LeaveQuota[]>('/hr/leave/quotas').then((r) => r.data),
  createLeaveQuota: (body: LeaveQuotaInput) =>
    api.post<LeaveQuota>('/hr/leave/quotas', body).then((r) => r.data),
  updateLeaveQuota: (id: number, body: LeaveQuotaInput) =>
    api.put<LeaveQuota>(`/hr/leave/quotas/${id}`, body).then((r) => r.data),
  toggleLeaveQuota: (id: number, value: boolean) =>
    api.patch<LeaveQuota>(`/hr/leave/quotas/${id}/active`, null, { params: { value } }).then((r) => r.data),
  deleteLeaveQuota: (id: number) => api.delete<void>(`/hr/leave/quotas/${id}`).then((r) => r.data),

  // leave requests
  listLeaveRequests: (params: {
    from?: string;
    to?: string;
    staffId?: number;
    leaveTypeId?: number;
    status?: LeaveStatus;
  }) => api.get<LeaveRequest[]>('/hr/leave/requests', { params }).then((r) => r.data),
  createLeaveRequest: (body: CreateLeaveRequestInput) =>
    api.post<LeaveRequest>('/hr/leave/requests', body).then((r) => r.data),
  approveLeaveRequest: (id: number) =>
    api.post<LeaveRequest>(`/hr/leave/requests/${id}/approve`, {}).then((r) => r.data),
  rejectLeaveRequest: (id: number, reason: string) =>
    api.post<LeaveRequest>(`/hr/leave/requests/${id}/reject`, { reason }).then((r) => r.data),
  cancelLeaveRequest: (id: number) =>
    api.post<LeaveRequest>(`/hr/leave/requests/${id}/cancel`).then((r) => r.data),

  // leave balances
  staffBalances: (staffId: number, academicYearId: number) =>
    api.get<LeaveBalance[]>(`/hr/leave/balances/staff/${staffId}`, { params: { academicYearId } }).then((r) => r.data),
  yearBalances: (academicYearId: number) =>
    api.get<LeaveBalance[]>('/hr/leave/balances', { params: { academicYearId } }).then((r) => r.data),
  allocateBalances: (body: { academicYearId: number; carryForward: boolean; previousAcademicYearId?: number | null }) =>
    api.post<number>('/hr/leave/balances/allocate', body).then((r) => r.data),

  // salary components
  listSalaryComponents: (activeOnly = false) =>
    api.get<SalaryComponent[]>('/hr/payroll/components', { params: { activeOnly } }).then((r) => r.data),
  createSalaryComponent: (body: SalaryComponentInput) =>
    api.post<SalaryComponent>('/hr/payroll/components', body).then((r) => r.data),
  updateSalaryComponent: (id: number, body: SalaryComponentInput) =>
    api.put<SalaryComponent>(`/hr/payroll/components/${id}`, body).then((r) => r.data),
  toggleSalaryComponent: (id: number, value: boolean) =>
    api.patch<SalaryComponent>(`/hr/payroll/components/${id}/active`, null, { params: { value } }).then((r) => r.data),

  // salary structures
  currentStructure: (staffId: number) =>
    api.get<SalaryStructure | null>(`/hr/payroll/structures/staff/${staffId}/current`).then((r) => r.data),
  structureHistory: (staffId: number) =>
    api.get<SalaryStructure[]>(`/hr/payroll/structures/staff/${staffId}/history`).then((r) => r.data),
  setStructure: (staffId: number, body: SalaryStructureInput) =>
    api.post<SalaryStructure>(`/hr/payroll/structures/staff/${staffId}`, body).then((r) => r.data),

  // advances
  listAdvances: (from: string, to: string) =>
    api.get<SalaryAdvance[]>('/hr/payroll/advances', { params: { from, to } }).then((r) => r.data),
  staffAdvances: (staffId: number) =>
    api.get<SalaryAdvance[]>(`/hr/payroll/advances/staff/${staffId}`).then((r) => r.data),
  addAdvance: (body: SalaryAdvanceInput) =>
    api.post<SalaryAdvance>('/hr/payroll/advances', body).then((r) => r.data),

  // payroll runs
  listRuns: () => api.get<PayrollRun[]>('/hr/payroll/runs').then((r) => r.data),
  getRun: (id: number) => api.get<PayrollRun>(`/hr/payroll/runs/${id}`).then((r) => r.data),
  createRun: (body: { academicYearId: number; payrollMonth: string }) =>
    api.post<PayrollRun>('/hr/payroll/runs', body).then((r) => r.data),
  finalizeRun: (id: number, body: { bankAccountId: number }) =>
    api.post<PayrollRun>(`/hr/payroll/runs/${id}/finalize`, body).then((r) => r.data),
  reverseRun: (id: number, reason: string) =>
    api.post<PayrollRun>(`/hr/payroll/runs/${id}/reverse`, { reason }).then((r) => r.data),
  runItems: (id: number) =>
    api.get<PayrollItem[]>(`/hr/payroll/runs/${id}/items`).then((r) => r.data),
  updateItem: (id: number, body: PayrollItemInput) =>
    api.put<PayrollItem>(`/hr/payroll/items/${id}`, body).then((r) => r.data),

  // evaluation criteria
  listCriteria: (activeOnly = false) =>
    api.get<EvaluationCriteria[]>('/hr/evaluation-criteria', { params: { activeOnly } }).then((r) => r.data),
  createCriteria: (body: EvaluationCriteriaInput) =>
    api.post<EvaluationCriteria>('/hr/evaluation-criteria', body).then((r) => r.data),
  updateCriteria: (id: number, body: EvaluationCriteriaInput) =>
    api.put<EvaluationCriteria>(`/hr/evaluation-criteria/${id}`, body).then((r) => r.data),
  toggleCriteria: (id: number, value: boolean) =>
    api
      .patch<EvaluationCriteria>(`/hr/evaluation-criteria/${id}/active`, null, { params: { value } })
      .then((r) => r.data),

  // performance evaluations
  listEvalsForStaff: (staffId: number) =>
    api.get<PerformanceEvaluation[]>(`/hr/staff/${staffId}/evaluations`).then((r) => r.data),
  listEvalsForYear: (academicYearId: number) =>
    api.get<PerformanceEvaluation[]>('/hr/evaluations', { params: { academicYearId } }).then((r) => r.data),
  getEvaluation: (id: number) =>
    api.get<PerformanceEvaluation>(`/hr/evaluations/${id}`).then((r) => r.data),
  createEvaluation: (body: PerformanceEvaluationInput) =>
    api.post<PerformanceEvaluation>('/hr/evaluations', body).then((r) => r.data),
  updateEvaluation: (id: number, body: PerformanceEvaluationInput) =>
    api.put<PerformanceEvaluation>(`/hr/evaluations/${id}`, body).then((r) => r.data),
  setEvaluationShared: (id: number, value: boolean) =>
    api
      .patch<PerformanceEvaluation>(`/hr/evaluations/${id}/shared`, null, { params: { value } })
      .then((r) => r.data),

  // staff documents
  listStaffDocuments: (staffId: number) =>
    api.get<StaffDocument[]>(`/hr/staff/${staffId}/documents`).then((r) => r.data),
  addStaffDocument: (staffId: number, body: StaffDocumentInput) =>
    api.post<StaffDocument>(`/hr/staff/${staffId}/documents`, body).then((r) => r.data),
  deleteStaffDocument: (staffId: number, docId: number) =>
    api.delete<void>(`/hr/staff/${staffId}/documents/${docId}`).then((r) => r.data),
};
